import React, { useState, useEffect } from 'react';
import { Moon, Sun, RefreshCw, Circle } from 'lucide-react';
import dayjs from 'dayjs';
import { checkHealth } from '../../services/api';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import { cn } from '../../lib/utils';

function Header({ darkMode, setDarkMode }) {
  const [currentTime, setCurrentTime] = useState(
    dayjs().format('MMM DD, YYYY HH:mm:ss')
  );
  const [serviceStatus, setServiceStatus] = useState('checking');

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(dayjs().format('MMM DD, YYYY HH:mm:ss'));
    }, 1000);

    const check = async () => {
      try {
        const response = await checkHealth();
        setServiceStatus(response.data.status === 'healthy' ? 'active' : 'inactive');
      } catch {
        setServiceStatus('error');
      }
    };
    check();
    const healthInterval = setInterval(check, 30000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(healthInterval);
    };
  }, []);

  const statusMeta = {
    active: { label: 'Monitoring Active', dot: 'text-success', ring: 'ring-success/30 bg-success/10 text-success' },
    inactive: { label: 'Monitoring Inactive', dot: 'text-warning', ring: 'ring-warning/30 bg-warning/10 text-warning' },
    error: { label: 'Service Offline', dot: 'text-destructive', ring: 'ring-destructive/30 bg-destructive/10 text-destructive' },
    checking: { label: 'Checking...', dot: 'text-muted-foreground', ring: 'ring-border bg-muted text-muted-foreground' },
  }[serviceStatus];

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('darkMode', next);
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 border-b border-border bg-background/80 px-6 backdrop-blur-md">
      <div className="flex flex-1 items-center gap-3">
        <h1 className="text-base font-semibold tracking-tight text-foreground">
          Activity Monitor
        </h1>
      </div>

      <div className="flex items-center gap-3">
        <div
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1',
            statusMeta.ring
          )}
        >
          <Circle className={cn('h-2 w-2 fill-current', statusMeta.dot)} />
          {statusMeta.label}
        </div>

        <span className="hidden text-sm tabular-nums text-muted-foreground md:inline">
          {currentTime}
        </span>

        <Tooltip label={darkMode ? 'Light mode' : 'Dark mode'} side="bottom">
          <Button variant="ghost" size="icon-sm" onClick={toggleTheme} aria-label="Toggle theme">
            {darkMode ? <Sun /> : <Moon />}
          </Button>
        </Tooltip>

        <Tooltip label="Refresh data" side="bottom">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => window.location.reload()}
            aria-label="Refresh"
          >
            <RefreshCw />
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}

export default Header;
